// UI strings for form recordings (taivutusten äänitykset, src/formvoices/): recording every form
// of an inflection table, hearing each other's takes, and the quick take on a form drill.
// Finnish (default) and English side by side. Style: docs/FINNISH-STYLE.md. Names (from /api/me)
// are never inflected: each sentence is built so a name stands in the nominative.
import { defineArea } from '../area';

export const formVoicesFi = {
  /** The table's mode switch (normal reading stays uncluttered). */
  mode: 'Äänitä taivutukset',
  recordAll: 'Äänitä kaikki',
  /** The table's note in recording mode. */
  tapHint: 'Napauta muotoa, niin voit äänittää sen ja kuunnella äänitykset. Sarakkeen otsikosta kuulet koko sarakkeen.',
  listenAll: 'Kuuntele kaikki',
  /** The voice choice for "Kuuntele kaikki". */
  whose: 'Kenen äänitykset',
  you: 'Sinä',
  /** Column header button, e.g. "Kuuntele kaikki: monikko · Roosa". */
  listenColumn: (col: string, who: string) => `Kuuntele kaikki: ${col} · ${who}`,
  stopListening: 'Lopeta kuuntelu',
  /** Appended to a cell's accessible name. */
  recordedBy: (who: string[]) => (who.length ? `äänitykset: ${who.join(' ja ')}` : 'ei äänityksiä'),
  olderForm: 'aiempi muoto',
  /** Legend under the toolbar: the ticks in the cells. */
  legendYou: 'sinä',
  legendOlder: 'aiempi muoto',
  // the form sheet
  notYet: 'Kumpikaan ei ole vielä äänittänyt tätä muotoa.',
  youOlder: (old: string) => `Äänitit tähän kohtaan aiemman muodon ${old}. Äänitä uusi.`,
  partnerOlder: (name: string, old: string) => `${name} äänitti tähän kohtaan aiemman muodon ${old}.`,
  waiting: 'odottaa yhteyttä',
  queued: 'Tallennettu tälle laitteelle. Lähtee kumppanin kuultavaksi, kun yhteys palaa.',
  analyse: 'Ääntämisanalyysi',
  analysing: 'Haetaan äänitystä…',
  analyseFailed: 'Äänitystä ei saatu haettua. Yritä uudelleen, kun yhteys toimii.',
  // the guided run
  runTitle: (hv: string) => `Äänitä kaikki · ${hv}`,
  step: (i: number, n: number) => `${i} / ${n}`,
  saveNext: 'Valmis ja seuraava',
  skip: 'Ohita',
  back: 'Edellinen',
  stop: 'Lopeta',
  already: 'Olet jo äänittänyt tämän muodon.',
  doneTitle: 'Kaikki muodot käyty läpi',
  doneCount: (n: number) => (n === 0 ? 'Et äänittänyt tällä kertaa yhtään muotoa.' : n === 1 ? 'Äänitit 1 muodon.' : `Äänitit ${n} muotoa.`),
  again: 'Aloita alusta',
  // form drills
  drillRecord: 'Äänitä tämä muoto',
};

export type FormVoicesMessages = typeof formVoicesFi;

export const formVoicesEn: FormVoicesMessages = {
  mode: 'Record the forms',
  recordAll: 'Record them all',
  tapHint: 'Tap a form to record it and hear the recordings. A column header plays the whole column.',
  listenAll: 'Play them all',
  whose: 'Whose recordings',
  you: 'You',
  listenColumn: (col, who) => `Play them all: ${col} · ${who}`,
  stopListening: 'Stop playing',
  recordedBy: (who) => (who.length ? `recordings: ${who.join(' and ')}` : 'no recordings'),
  olderForm: 'older form',
  legendYou: 'you',
  legendOlder: 'older form',
  notYet: 'Neither of you has recorded this form yet.',
  youOlder: (old) => `You recorded the earlier form ${old} here. Record the new one.`,
  partnerOlder: (name, old) => `${name} recorded the earlier form ${old} here.`,
  waiting: 'waiting for a connection',
  queued: 'Saved on this device. It goes to your partner when the connection is back.',
  analyse: 'Pronunciation analysis',
  analysing: 'Fetching the recording…',
  analyseFailed: 'The recording could not be fetched. Try again when you are online.',
  runTitle: (hv) => `Record them all · ${hv}`,
  step: (i, n) => `${i} / ${n}`,
  saveNext: 'Done, next',
  skip: 'Skip',
  back: 'Previous',
  stop: 'Stop',
  already: 'You have recorded this form already.',
  doneTitle: 'Every form done',
  doneCount: (n) => (n === 0 ? 'You recorded no forms this time.' : n === 1 ? 'You recorded 1 form.' : `You recorded ${n} forms.`),
  again: 'Start over',
  drillRecord: 'Record this form',
};

// Messages.formVoices (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('formVoices', formVoicesFi, formVoicesEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof formVoicesFi> {}
}
